// Shared by the Leave page and a staff member's Leave tab, so a request looks, reads and is decided
// the same way in both places: labelled Approve / Decline buttons, the roster impact shown BEFORE
// approving, and a reason captured when declining.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import { Check, X as XIcon, AlertTriangle, Trash2, CalendarOff } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Dialog, DetailDrawer, Field, FormRow, StatusPill, fmtDate, fmtTimeRange, plural, num,
} from '../../components/ui';
import { LEAVE_TYPES, leaveTypeLabel, ReasonDialog, useStableCallback, toDay } from '../Staff/hrKit';

export const leaveStatusView = (s) => {
  if (s === 'declined' || s === 'rejected') return { label: 'Declined', tone: 'danger' };
  if (s === 'approved') return { label: 'Approved', tone: 'success' };
  if (s === 'pending') return { label: 'Pending', tone: 'warning' };
  return { label: s ? String(s) : 'Pending', tone: 'neutral' };
};
export const LeaveStatus = ({ status }) => {
  const v = leaveStatusView(status);
  return <StatusPill status={status} label={v.label} tone={v.tone} />;
};
export const isDeclined = (s) => s === 'declined' || s === 'rejected';

// Calendar days, inclusive — the count the request form has always shown.
export function leaveDays(start, end) {
  const a = toDay(start); const b = toDay(end);
  if (!a || !b) return null;
  return Math.max(1, differenceInDays(b, a) + 1);
}

const WD = (d) => d.toLocaleDateString('en-AU', { weekday: 'short' });
const MON = (d) => d.toLocaleDateString('en-AU', { month: 'short' });
// 'Tue 7 Oct 2026' · 'Tue 7 – Thu 9 Oct 2026' · 'Tue 30 Sep – Thu 2 Oct 2026'
export function fmtPeriod(start, end) {
  const a = toDay(start); const b = toDay(end) || a;
  if (!a) return '—';
  if (a.getTime() === b.getTime()) return `${WD(a)} ${a.getDate()} ${MON(a)} ${a.getFullYear()}`;
  if (a.getFullYear() === b.getFullYear()) {
    if (a.getMonth() === b.getMonth()) return `${WD(a)} ${a.getDate()} – ${WD(b)} ${b.getDate()} ${MON(b)} ${b.getFullYear()}`;
    return `${WD(a)} ${a.getDate()} ${MON(a)} – ${WD(b)} ${b.getDate()} ${MON(b)} ${b.getFullYear()}`;
  }
  return `${WD(a)} ${fmtDate(a)} – ${WD(b)} ${fmtDate(b)}`;
}
const overlaps = (r, from, to) => {
  const s = String(r.start_date || '').slice(0, 10); const e = String(r.end_date || '').slice(0, 10);
  return s <= to && e >= from;
};
export const inRange = (r, from, to) => overlaps(r, from || '0000-01-01', to || '9999-12-31');

// Hours to at most 2 decimals, without padding: 7.6, 38, 15.25.
export const fmtLeaveHours = (h) => new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 }).format(Number(h) || 0);
// What the hours box holds, as a number the API accepts, or null when it isn't one.
export const parseLeaveHours = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 9999.99 ? Math.round(n * 100) / 100 : null;
};
// Where the suggested hours came from, in words.
export const suggestedBasis = (sg) => (!sg ? ''
  : sg.basis === 'roster' ? `Their rostered hours in this period (${plural(sg.shifts, 'shift')}).`
    : `No shifts rostered: ${plural(sg.weekdays, 'weekday')} × 7.6 hours.`);

/* ── Request leave ─────────────────────────────────────────────────────── */

export function LeaveRequestDialog({ open, onClose, onSaved, staff = [], fixedStaff, canPickStaff }) {
  const [form, setForm] = useState({ staffId: '', leaveType: 'annual', startDate: '', endDate: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm({ staffId: fixedStaff ? fixedStaff.id : '', leaveType: 'annual', startDate: '', endDate: '', notes: '' });
  }, [open, fixedStaff]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });
  const days = form.startDate && form.endDate && form.endDate >= form.startDate ? leaveDays(form.startDate, form.endDate) : 0;

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (canPickStaff && !fixedStaff && !form.staffId) errs.staffId = 'Choose who the leave is for.';
    if (!form.startDate) errs.startDate = 'Choose the first day of leave.';
    if (!form.endDate) errs.endDate = 'Choose the last day of leave.';
    else if (form.startDate && form.endDate < form.startDate) errs.endDate = 'The last day is before the first day.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.post('/leave', { ...form, staffId: fixedStaff ? fixedStaff.id : form.staffId || undefined });
      toast.success('Leave request submitted');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setErrors({ form: err.response?.data?.message || "The request wasn't submitted. Try again." });
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="sm" title="Request leave"
      description={fixedStaff ? `For ${fixedStaff.first_name} ${fixedStaff.last_name}` : undefined}
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="leave-request" className="btn-primary" disabled={saving}>{saving ? 'Submitting…' : 'Submit request'}</button>
        </>
      )}>
      <form id="leave-request" onSubmit={save} className="space-y-4">
        {canPickStaff && !fixedStaff && (
          <Field label="Staff member" required error={errors.staffId}>
            {(id) => (
              <select id={id} className="input-field" value={form.staffId} onChange={(e) => set('staffId', e.target.value)}>
                <option value="">Choose…</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            )}
          </Field>
        )}
        <Field label="Leave type">
          {(id) => (
            <select id={id} className="input-field" value={form.leaveType} onChange={(e) => set('leaveType', e.target.value)}>
              {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          )}
        </Field>
        <FormRow>
          <Field label="First day" required error={errors.startDate}>
            {(id) => <input id={id} type="date" className="input-field" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />}
          </Field>
          <Field label="Last day" required error={errors.endDate}>
            {(id) => <input id={id} type="date" className="input-field" min={form.startDate || undefined} value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />}
          </Field>
        </FormRow>
        {days > 0 && <p className="text-[13px] text-slate-600">{fmtPeriod(form.startDate, form.endDate)} · <span className="font-semibold tabular-nums text-slate-900">{plural(days, 'day')}</span></p>}
        <Field label="Notes">
          {(id) => <textarea id={id} className="input-field resize-y" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />}
        </Field>
      </form>
    </Dialog>
  );
}

/* ── Decisions ─────────────────────────────────────────────────────────── */

// approve(r, hours) confirms with the real roster impact and the hours coming off the balance;
// decline(r) asks for an optional reason. `hours` is what the reviewer typed in the review drawer;
// without it (the Approve button on a list row) the suggested hours are deducted.
export function useLeaveDecisions(onChanged) {
  const confirm = useConfirm();
  const [declining, setDeclining] = useState(null);
  const [busy, setBusy] = useState(false);

  const approve = useCallback(async (r, typedHours) => {
    let shifts = null; let suggested = null;
    try {
      const { data } = await api.get(`/leave/${r.id}/conflicts`);
      shifts = data.shifts || []; suggested = data.suggested || null;
    } catch { shifts = null; }
    const typed = typedHours === undefined ? null : parseLeaveHours(typedHours);
    if (typedHours !== undefined && typed === null) { toast.error('Enter the hours to deduct as a number, e.g. 7.6.'); return false; }
    const hours = typed !== null ? typed : (suggested ? suggested.hours : null);
    const hoursNote = hours === null ? ''
      : `\n\n${fmtLeaveHours(hours)} hours come off their ${leaveTypeLabel(r.leave_type).toLowerCase()} leave balance.${typed === null ? ' To change the hours, open the request instead.' : ''}`;
    const name = r.staff_name || 'this worker';
    const d = leaveDays(r.start_date, r.end_date);
    const n = shifts ? shifts.length : Number(r.conflict_count) || 0;
    const list = shifts && shifts.length
      ? `\n\n${shifts.slice(0, 6).map((s) => `• ${fmtPeriod(s.shift_date, s.shift_date)}, ${fmtTimeRange(s.start_time, s.end_time)}${s.client_name ? ` with ${s.client_name}` : ''}`).join('\n')}${shifts.length > 6 ? `\n• and ${shifts.length - 6} more` : ''}\n\nThey become vacant and show on the Job Board so you can find cover.`
      : '';
    const msg = `${leaveTypeLabel(r.leave_type)} leave, ${fmtPeriod(r.start_date, r.end_date)} (${plural(d || 1, 'day')}).${n ? '' : ' No rostered shifts are affected.'}${list}${shifts === null ? '\n\nWe couldn\'t check the roster just now; any shifts in this period will be unassigned.' : ''}${hoursNote}`;
    const ok = await confirm(msg, {
      title: n ? `Approve and unassign ${plural(n, 'shift')}?` : `Approve leave for ${name}?`,
      confirmLabel: n ? `Approve and unassign ${num(n)}` : 'Approve',
    });
    if (!ok) return false;
    try {
      const { data } = await api.put(`/leave/${r.id}/approve`, hours !== null ? { hoursTaken: hours } : {});
      const cs = (data && data.conflictingShifts) || [];
      if (data && data.alreadyApproved) toast.success('This leave was already approved');
      else if (cs.length) toast.success(`Leave approved. ${plural(cs.length, 'shift')} unassigned and now on the Job Board.`);
      else toast.success('Leave approved');
      onChanged && onChanged();
      return true;
    } catch (e) { toast.error(e.response?.data?.message || "The leave wasn't approved. Try again."); return false; }
  }, [confirm, onChanged]);

  const doDecline = async (reason) => {
    setBusy(true);
    try {
      await api.put(`/leave/${declining.id}/reject`, reason ? { reason } : {});
      toast.success('Leave declined');
      setDeclining(null);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || "The leave wasn't declined. Try again."); }
    finally { setBusy(false); }
  };

  const declineDialog = (
    <ReasonDialog
      open={!!declining}
      onClose={() => setDeclining(null)}
      onConfirm={doDecline}
      busy={busy}
      danger
      title={declining ? `Decline leave for ${declining.staff_name || 'this worker'}?` : ''}
      description={declining ? `${leaveTypeLabel(declining.leave_type)} leave, ${fmtPeriod(declining.start_date, declining.end_date)}. They're emailed that it was declined.` : ''}
      label="Reason"
      placeholder="e.g. Not enough cover that week"
      confirmLabel="Decline"
    />
  );
  return { approve, decline: setDeclining, declineDialog };
}

/* ── Review drawer ─────────────────────────────────────────────────────── */

export function LeaveReviewDrawer({ request, allRequests = [], canReview, canDelete, onApprove, onDecline, onDelete, onClose }) {
  const [shifts, setShifts] = useState(null);
  const [shiftsError, setShiftsError] = useState(false);
  const [balances, setBalances] = useState([]);
  // Hours to deduct on approval: pre-filled with the server's suggestion, editable.
  const [suggested, setSuggested] = useState(null);
  const [hours, setHours] = useState('');
  const close = useStableCallback(onClose);

  useEffect(() => {
    setShifts(null); setShiftsError(false); setBalances([]); setSuggested(null); setHours('');
    if (!request) return;
    if (request.status === 'pending' && canReview) {
      api.get(`/leave/${request.id}/conflicts`).then((r) => {
        setShifts(r.data.shifts || []);
        if (r.data.suggested) { setSuggested(r.data.suggested); setHours(String(r.data.suggested.hours)); }
      }).catch(() => setShiftsError(true));
    }
    api.get(`/leave/balance/${request.staff_id}`).then((r) => setBalances(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [request, canReview]);

  if (!request) return null;
  const from = String(request.start_date).slice(0, 10); const to = String(request.end_date).slice(0, 10);
  const others = allRequests.filter((o) => o.id !== request.id && o.staff_id !== request.staff_id
    && (o.status === 'approved' || o.status === 'pending') && overlaps(o, from, to));
  const d = leaveDays(request.start_date, request.end_date);
  const hoursError = suggested && parseLeaveHours(hours) === null ? 'Enter the hours to deduct, e.g. 7.6 (0 for none).' : null;
  // Until the suggestion arrives (or if it couldn't), approval falls back to the server's own suggestion.
  const approveNow = () => onApprove(request, suggested ? hours : undefined);

  return (
    <DetailDrawer
      open={!!request}
      onClose={close}
      width="md"
      title={request.staff_name || 'Leave request'}
      subtitle={`${leaveTypeLabel(request.leave_type)} leave`}
      statusNode={<LeaveStatus status={request.status} />}
      meta={[
        { label: 'Period', value: fmtPeriod(request.start_date, request.end_date) },
        { label: 'Days', value: d ? num(d) : null },
        { label: 'Requested', value: request.created_at ? fmtDate(request.created_at) : null },
        request.approved_by_name && { label: isDeclined(request.status) ? 'Declined by' : 'Approved by', value: request.approved_by_name },
        request.status === 'approved' && request.hours_taken != null && { label: 'Hours deducted', value: `${fmtLeaveHours(request.hours_taken)} h` },
      ].filter(Boolean)}
      footer={(
        <>
          {canDelete && <button type="button" onClick={() => onDelete(request)} className="btn-ghost btn-sm mr-auto !text-rose-700"><Trash2 size={13} aria-hidden="true" />Delete</button>}
          {canReview && request.status === 'pending' && (
            <>
              <button type="button" onClick={() => onDecline(request)} className="btn-secondary btn-sm"><XIcon size={13} aria-hidden="true" />Decline</button>
              <button type="button" onClick={approveNow} disabled={!!hoursError} className="btn-primary btn-sm"><Check size={13} aria-hidden="true" />Approve</button>
            </>
          )}
        </>
      )}
    >
      <div className="space-y-6 text-sm">
        {request.notes && (
          <section><h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Notes</h4><p className="whitespace-pre-wrap text-slate-800">{request.notes}</p></section>
        )}
        {isDeclined(request.status) && request.decline_reason && (
          <section><h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Reason declined</h4><p className="whitespace-pre-wrap text-slate-800">{request.decline_reason}</p></section>
        )}
        {request.status === 'pending' && canReview && (
          <section>
            <h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Shifts affected {shifts ? `(${num(shifts.length)})` : ''}</h4>
            {shiftsError ? <p className="text-rose-700">We couldn't check the roster. Try reopening this request.</p>
              : shifts === null ? <div className="skeleton h-10 w-full" />
                : shifts.length === 0 ? <p className="text-slate-600">No rostered shifts in this period.</p> : (
                  <>
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {shifts.map((s) => (
                        <li key={s.id} className="flex flex-wrap items-center gap-x-2 px-3 py-2 text-[13px]">
                          <span className="font-medium text-slate-800">{fmtPeriod(s.shift_date, s.shift_date)}</span>
                          <span className="text-slate-600">{fmtTimeRange(s.start_time, s.end_time)}</span>
                          {s.client_name && <span className="text-slate-500">· {s.client_name}</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 flex items-start gap-1.5 text-[12.5px] text-amber-800">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                      Approving unassigns these shifts. They then show on the <Link to="/job-board" className="font-medium underline">Job Board</Link> so you can find cover.
                    </p>
                  </>
                )}
          </section>
        )}
        {request.status === 'pending' && canReview && suggested && (
          <section>
            <Field label="Hours to deduct" error={hoursError}
              hint={`${suggestedBasis(suggested)} Comes off their ${leaveTypeLabel(request.leave_type).toLowerCase()} leave balance when you approve.`}>
              {(id) => (
                <div className="flex items-center gap-2">
                  <input id={id} type="number" min="0" max="9999.99" step="0.01" inputMode="decimal"
                    className="input-field w-28 text-right tabular-nums" value={hours} onChange={(e) => setHours(e.target.value)} />
                  <span className="text-slate-600">hours</span>
                  {String(suggested.hours) !== hours && (
                    <button type="button" onClick={() => setHours(String(suggested.hours))} className="text-[12px] font-medium text-slate-500 hover:text-slate-800 hover:underline">
                      Reset to {fmtLeaveHours(suggested.hours)}
                    </button>
                  )}
                </div>
              )}
            </Field>
          </section>
        )}
        <section>
          <h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Also away then</h4>
          {others.length === 0 ? <p className="text-slate-600">No one else has leave on these days.</p> : (
            <ul className="space-y-1">
              {others.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center gap-x-2 text-[13px]">
                  <CalendarOff size={13} className="text-slate-400" aria-hidden="true" />
                  <span className="font-medium text-slate-800">{o.staff_name}</span>
                  <span className="text-slate-600">{fmtPeriod(o.start_date, o.end_date)}</span>
                  <LeaveStatus status={o.status} />
                </li>
              ))}
            </ul>
          )}
        </section>
        {balances.length > 0 && (
          <section>
            <h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Leave balance this year</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-slate-200 text-left text-[12px] text-slate-500"><th className="py-1.5 pr-3 font-medium">Type</th><th className="py-1.5 pr-3 text-right font-medium">Entitled</th><th className="py-1.5 pr-3 text-right font-medium">Taken</th><th className="py-1.5 text-right font-medium">Remaining</th></tr></thead>
                <tbody>
                  {balances.map((b) => (
                    <tr key={b.leave_type} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3">{leaveTypeLabel(b.leave_type)}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{fmtLeaveHours(b.entitlement_hours)} h</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{fmtLeaveHours(b.taken_hours)} h</td>
                      <td className="py-1.5 text-right tabular-nums font-medium">{fmtLeaveHours(b.remaining_hours)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </DetailDrawer>
  );
}
